/***
 *
 *   AUTH BRANDING TICKET BACKDROP
 *   A sparse field of outlined tickets drifting behind the auth/onboarding
 *   branding panel. Deliberately near-invisible: it should register as texture,
 *   not as motion. Replaces the earlier repeating icon grid.
 *
 **********/

import Style from './brandingBackdrop.tailwind';

// Hand-placed rather than random so the composition is stable across renders
// and nothing lands in a clump. `top`/`left` are percentages of the overscanned
// frame; `width` is rem. Smaller tickets read as further away, so they are
// fainter and drift slower. Delays are negative to start each ticket mid-cycle
// — otherwise the whole field would launch in unison on mount. Durations are
// deliberately close but never equal, so the field stays out of phase.
const TICKETS = [
    { left: 12, top: 8, width: 9, tilt: -8, opacity: 0.15, duration: 13, delay: -4 },
    { left: 74, top: 15, width: 7, tilt: 10, opacity: 0.11, duration: 16, delay: -11 },
    { left: 30, top: 27, width: 11, tilt: -4, opacity: 0.19, duration: 10, delay: -2 },
    { left: 86, top: 38, width: 8.5, tilt: -12, opacity: 0.14, duration: 14, delay: -8 },
    { left: 8, top: 48, width: 6.5, tilt: 6, opacity: 0.1, duration: 17, delay: -7 },
    { left: 62, top: 58, width: 10, tilt: 8, opacity: 0.17, duration: 11, delay: -13 },
    { left: 22, top: 72, width: 8, tilt: -6, opacity: 0.13, duration: 15, delay: -3 },
    { left: 78, top: 84, width: 12, tilt: 4, opacity: 0.18, duration: 12, delay: -9 },
    { left: 40, top: 93, width: 7, tilt: -10, opacity: 0.11, duration: 18, delay: -5 },
];

// Stroke-only ticket: rounded body with a semicircular notch bitten out of the
// top and bottom edges at the perforation, so no fill is needed and the panel
// gradient shows straight through.
const TICKET_PATH = `M 8 0
    H 78 A 6 6 0 0 0 90 0
    H 112 A 8 8 0 0 1 120 8
    V 56 A 8 8 0 0 1 112 64
    H 90 A 6 6 0 0 0 78 64
    H 8 A 8 8 0 0 1 0 56
    V 8 A 8 8 0 0 1 8 0 Z`;

function Ticket() {
    return (
        // Padded viewBox: the path runs edge to edge, so a bare 120x64 box
        // would clip half the stroke off every side.
        <svg viewBox="0 0 122 66" className={Style.svg} aria-hidden focusable="false">
            <g transform="translate(1, 1)" strokeWidth="1.5" strokeLinecap="round">
                <path d={TICKET_PATH} className={Style.outline} />
                <path d="M 84 10 V 54" className={Style.perforation} />
                <path d="M 14 24 H 62" className={Style.rule} />
                <path d="M 14 38 H 50" className={Style.rule} />
            </g>
        </svg>
    );
}

export function AuthBrandingTicketBackdrop() {
    return (
        <div aria-hidden className={Style.root}>
            <div className={Style.frame}>
                {TICKETS.map((ticket, i) => (
                    <div
                        key={i}
                        className={Style.ticket}
                        style={{
                            left: `${ticket.left}%`,
                            top: `${ticket.top}%`,
                            width: `${ticket.width}rem`,
                            opacity: ticket.opacity,
                        }}>
                        <div
                            className={Style.ticketMotion}
                            style={{
                                // Read back by the ticket-float keyframes so each
                                // ticket keeps its resting angle while drifting.
                                '--ticket-tilt': `${ticket.tilt}deg`,
                                transform: `rotate(${ticket.tilt}deg)`,
                                animationDuration: `${ticket.duration}s`,
                                animationDelay: `${ticket.delay}s`,
                            }}>
                            <Ticket />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
